import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { SIDEBAR_STRUCTURE } from "../../config/navigation.js";
import { renderIcon } from "../../utils/icons.js";
import { ModalManager } from "../../utils/ModalManager.js";
import { InputModal } from "../../components/modals/InputModal.js";
import { AlertModal } from "../../components/modals/AlertModal.js";
import { locale } from "../../services/LocaleService.js";

const db = getFirestore(app);

export class AdminMenuEditor {
    constructor(container) {
        this.container = container;
        this.navRef = doc(db, "settings", "navigation"); // [FIX] Standardize to 'settings'
        this.masterItems = SIDEBAR_STRUCTURE;
        this.currentOrder = []; // Array of { type, id? }

        // Items map for easy lookup
        this.itemMap = new Map();
        this.masterItems.forEach(item => {
            if (item.id) this.itemMap.set(item.id, item);
        });

        this.init();
    }

    async init() {
        this.renderLoading();
        await this.loadOrder();
        this.renderEditor();
    }

    renderLoading() {
        this.container.innerHTML = `<div class="p-4 text-center text-gray-500">메뉴 설정을 불러오는 중입니다...</div>`;
    }

    async loadOrder() {
        try {
            const snap = await getDoc(this.navRef);
            if (snap.exists() && snap.data().sidebar_order) {
                const storedOrder = snap.data().sidebar_order;
                this.mergeOrder(storedOrder);
            } else {
                // Initial Default: Convert SIDEBAR_STRUCTURE to order format
                this.currentOrder = this.masterItems.map(item => {
                    if (item.type === 'DIVIDER') return { type: 'DIVIDER' };
                    if (item.type === 'EXTERNAL_LINKS_MARKER') return { type: 'EXTERNAL_LINKS_MARKER' };
                    return { type: 'ITEM', id: item.id };
                });
            }
        } catch (e) {
            console.error("Failed to load menu order", e);
            alert("메뉴 설정을 불러오지 못했습니다. 기본 순서로 표시됩니다.");
            // Fallback
            this.currentOrder = this.masterItems.map(item => {
                if (item.type === 'DIVIDER') return { type: 'DIVIDER' };
                if (item.type === 'EXTERNAL_LINKS_MARKER') return { type: 'EXTERNAL_LINKS_MARKER' };
                return { type: 'ITEM', id: item.id };
            });
        }
    }

    mergeOrder(storedOrder) {
        // 1. Keep valid stored items
        const newOrder = [];
        const processedIds = new Set();
        const processedDocIds = new Set(); // Track dynamic docs

        storedOrder.forEach(entry => {
            if (entry.type === 'DIVIDER' || entry.type === 'EXTERNAL_LINKS_MARKER') {
                newOrder.push(entry);

            } else if (entry.type === 'ITEM') {
                if (this.itemMap.has(entry.id)) {
                    // Standard Item
                    const mergedItem = {
                        type: 'ITEM',
                        id: entry.id,
                        customLabel: entry.customLabel || undefined
                    };
                    newOrder.push(mergedItem);
                    processedIds.add(entry.id);
                }
                // Removed items are skipped
            } else if (entry.type === 'MODAL' && entry.docId) {
                // [NEW] Preserve Dynamic Document Items
                newOrder.push(entry);
                processedDocIds.add(entry.docId);
            }
        });

        // 2. Append new items from code (Standard Items)
        this.masterItems.forEach(item => {
            if (item.id && !processedIds.has(item.id)) {
                newOrder.push({ type: 'ITEM', id: item.id });
            }
            // Note: We cannot "discover" new dynamic docs here effortlessly unless we fetch system_docs.
            // But 'Add to Menu' checkbox in AdminDocsPage handles the addition.
            // Here we just ensure we don't DELETE them.
        });

        this.currentOrder = newOrder;
    }

    renderEditor() {
        this.container.innerHTML = `
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div class="flex items-center justify-between mb-4">
                    <div>
                         <h2 class="text-lg font-bold text-gray-800">📋 메뉴 순서 편집 (Drag & Drop)</h2>
                         <p class="text-sm text-gray-500">메뉴 항목을 드래그하여 순서를 변경하세요.</p>
                    </div>
                    <div class="flex gap-2">
                        <button id="btn-add-divider" class="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-lg border border-gray-300 transition-colors">
                            + 구분선 추가
                        </button>
                        <button id="btn-save-menu" class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition-colors shadow-sm">
                            저장하기
                        </button>
                    </div>
                </div>

                <div id="menu-drag-list" class="space-y-2 border border-gray-100 bg-gray-50 p-2 rounded-xl min-h-[300px]">
                    <!-- Items go here -->
                </div>
            </div>
        `;

        this.listEl = this.container.querySelector('#menu-drag-list');
        this.btnSave = this.container.querySelector('#btn-save-menu');
        this.btnAddDivider = this.container.querySelector('#btn-add-divider');

        this.renderListItems();
        this.bindEvents();
    }

    renderListItems() {
        if (!this.listEl) return;
        this.listEl.innerHTML = '';

        // [DEBUG] Check data integrity
        if (!this.currentOrder || this.currentOrder.length === 0) {
            this.listEl.innerHTML = `<div class="p-4 text-center text-gray-500">메뉴 데이터가 없습니다. (Empty Order)</div>`;
            return;
        }

        try {
            this.currentOrder.forEach((entry, index) => {
                try {
                    const row = document.createElement('div');
                    row.className = "flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 shadow-sm cursor-grab active:cursor-grabbing hover:border-indigo-300 transition-colors";
                    row.draggable = true;
                    row.dataset.index = index;

                    if (entry.type === 'DIVIDER') {
                        row.className += " justify-center bg-gray-100";
                        row.innerHTML = `
                    <div class="h-0.5 w-full bg-gray-300 rounded mx-4"></div>
                    <span class="text-xs text-gray-400 font-mono shrink-0">DIVIDER</span>
                    <div class="h-0.5 w-full bg-gray-300 rounded mx-4"></div>
                    <button class="ml-2 text-gray-400 hover:text-red-500 p-1 btn-delete-item">
                        <span class="material-symbols-outlined text-sm">close</span>
                    </button>
                `;
                    } else if (entry.type === 'EXTERNAL_LINKS_MARKER') {
                        row.className += " border-dashed border-indigo-300 bg-indigo-50";
                        row.innerHTML = `
                    <span class="material-symbols-outlined text-indigo-500">link</span>
                    <span class="font-bold text-indigo-700 text-sm flex-1">외부 링크 영역 (Marker)</span>
                    <span class="text-xs text-indigo-400">이 위치에 외부 링크가 삽입됩니다.</span>
                `;
                    } else if (entry.type === 'MODAL' && entry.docId) {
                        // [NEW] Dynamic Document Item
                        row.innerHTML = `
                            <span class="material-symbols-outlined text-gray-400 cursor-grab">drag_indicator</span>
                            <div class="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                                <span class="material-symbols-outlined text-blue-600 text-sm">article</span>
                            </div>
                            <div class="flex-1 min-w-0">
                                <div class="font-bold text-gray-800 text-sm truncate flex items-center gap-2">
                                    ${entry.customLabel || entry.docId}
                                    <span class="text-[10px] bg-blue-100 text-blue-600 px-1 rounded border border-blue-200">문서</span>
                                </div>
                                <div class="text-xs text-gray-400 truncate">Doc: ${entry.docId}</div>
                            </div>
                            <button class="ml-2 text-gray-400 hover:text-red-500 p-1 btn-delete-item" title="메뉴에서 제거">
                                <span class="material-symbols-outlined text-sm">close</span>
                            </button>
                        `;

                        // Delete Handler (Remove from menu only)
                        const delBtn = row.querySelector('.btn-delete-item');
                        if (delBtn) {
                            delBtn.onclick = (e) => {
                                e.stopPropagation();
                                if (confirm("메뉴 목록에서만 제거됩니다.\n(문서는 삭제되지 않습니다)")) {
                                    this.currentOrder.splice(index, 1);
                                    this.renderListItems();
                                }
                            };
                        }

                    } else {
                        const navItem = this.itemMap.get(entry.id);
                        if (!navItem) {
                            row.innerHTML = `<span class="text-red-500">Unknown Item (${entry.id})</span>`;
                        } else {
                            const displayLabel = entry.customLabel || navItem.label;
                            const isCustom = !!entry.customLabel;

                            row.innerHTML = `
                        <span class="material-symbols-outlined text-gray-400 cursor-grab">drag_indicator</span>
                        <div class="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center shrink-0">
                            <!-- Icon Placeholder -->
                            <i data-lucide="${navItem.icon}" class="w-5 h-5 text-gray-600"></i>
                        </div>
                        <div class="flex-1 min-w-0">
                            <div class="font-bold text-gray-800 text-sm truncate flex items-center gap-2">
                                ${displayLabel} 
                                ${isCustom ? '<span class="text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded border border-indigo-200">수정됨</span>' : ''}
                                ${navItem.role ? `<span class="text-[10px] bg-gray-100 text-gray-500 px-1 rounded uppercase">${navItem.role}</span>` : ''}
                            </div>
                            <div class="text-xs text-gray-400 truncate">${navItem.path || entry.id}</div>
                        </div>
                        <button class="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors btn-edit-label" title="이름 변경">
                            <span class="material-symbols-outlined text-lg">edit</span>
                        </button>
                    `;
                            // Render Icon Safe
                            const iconContainer = row.querySelector('i');
                            if (iconContainer) {
                                const parent = iconContainer.parentElement;
                                parent.innerHTML = '';
                                try {
                                    renderIcon(parent, navItem.icon, { class: "w-5 h-5 text-gray-600" });
                                } catch (e) {
                                    console.warn(e);
                                }
                            }

                            // Edit Click Handler
                            const editBtn = row.querySelector('.btn-edit-label');
                            if (editBtn) {
                                editBtn.onclick = (e) => {
                                    e.stopPropagation();

                                    // [Improved UX] Custom VS Auto-Translate Selection
                                    const isCurrentlyCustom = !!entry.customLabel;
                                    const systemLabel = locale.t(`menu.${navItem.id}`) || navItem.label;

                                    const modalHtml = `
                                        <div class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" id="menu-edit-modal">
                                            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 transform transition-all scale-100 ring-1 ring-gray-900/5">
                                                <h3 class="text-lg font-bold text-gray-900 mb-1">메뉴 이름 설정</h3>
                                                <p class="text-xs text-gray-500 mb-4">다국어 지원을 위해 '자동 번역' 사용을 권장합니다.</p>
                                                
                                                <div class="space-y-4">
                                                    <!-- Option 1: Auto Translate -->
                                                    <label class="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${!isCurrentlyCustom ? 'bg-indigo-50 border-indigo-200 ring-1 ring-indigo-500' : 'bg-white border-gray-200 hover:bg-gray-50'}" id="opt-auto">
                                                        <input type="radio" name="menu_mode" value="auto" class="mt-1 text-indigo-600 focus:ring-indigo-500" ${!isCurrentlyCustom ? 'checked' : ''}>
                                                        <div class="flex-1">
                                                            <div class="font-bold text-sm text-gray-900">자동 번역 사용 (시스템 기본)</div>
                                                            <div class="text-xs text-gray-500 mt-1">
                                                                사용자의 언어 설정에 따라 자동으로 번역됩니다.<br>
                                                                <span class="text-indigo-600 font-mono text-[10px] mt-1 block">Preview: ${systemLabel} (Current)</span>
                                                            </div>
                                                        </div>
                                                    </label>

                                                    <!-- Option 2: Custom -->
                                                    <label class="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${isCurrentlyCustom ? 'bg-indigo-50 border-indigo-200 ring-1 ring-indigo-500' : 'bg-white border-gray-200 hover:bg-gray-50'}" id="opt-custom">
                                                        <input type="radio" name="menu_mode" value="custom" class="mt-1 text-indigo-600 focus:ring-indigo-500" ${isCurrentlyCustom ? 'checked' : ''}>
                                                        <div class="flex-1">
                                                            <div class="font-bold text-sm text-gray-900">직접 입력 (고정 텍스트)</div>
                                                            <div class="text-xs text-gray-500 mt-1">모든 언어에서 입력한 텍스트로 고정됩니다.</div>
                                                            <input type="text" id="custom-input" 
                                                                class="mt-2 w-full text-sm border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400"
                                                                placeholder="예: My Menu"
                                                                value="${entry.customLabel || ''}"
                                                                ${!isCurrentlyCustom ? 'disabled' : ''}
                                                            >
                                                        </div>
                                                    </label>
                                                </div>

                                                <div class="flex gap-2 mt-6">
                                                    <button id="btn-cancel-edit" class="flex-1 py-2.5 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">취소</button>
                                                    <button id="btn-save-edit" class="flex-1 py-2.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg shadow-indigo-500/30 transition-colors">적용하기</button>
                                                </div>
                                            </div>
                                        </div>
                                    `;

                                    // Append to body
                                    const wrapper = document.createElement('div');
                                    wrapper.innerHTML = modalHtml;
                                    document.body.appendChild(wrapper);

                                    // Logic
                                    const modalEl = document.getElementById('menu-edit-modal');
                                    const optAuto = document.getElementById('opt-auto');
                                    const optCustom = document.getElementById('opt-custom');
                                    const customInput = document.getElementById('custom-input');
                                    const radios = modalEl.querySelectorAll('input[name="menu_mode"]');

                                    const updateState = (val) => {
                                        if (val === 'auto') {
                                            customInput.disabled = true;
                                            optAuto.classList.add('bg-indigo-50', 'border-indigo-200', 'ring-1', 'ring-indigo-500');
                                            optAuto.classList.remove('bg-white', 'border-gray-200', 'hover:bg-gray-50');
                                            optCustom.classList.remove('bg-indigo-50', 'border-indigo-200', 'ring-1', 'ring-indigo-500');
                                            optCustom.classList.add('bg-white', 'border-gray-200', 'hover:bg-gray-50');
                                        } else {
                                            customInput.disabled = false;
                                            customInput.focus();
                                            optCustom.classList.add('bg-indigo-50', 'border-indigo-200', 'ring-1', 'ring-indigo-500');
                                            optCustom.classList.remove('bg-white', 'border-gray-200', 'hover:bg-gray-50');
                                            optAuto.classList.remove('bg-indigo-50', 'border-indigo-200', 'ring-1', 'ring-indigo-500');
                                            optAuto.classList.add('bg-white', 'border-gray-200', 'hover:bg-gray-50');
                                        }
                                    };

                                    // Toggle Logic
                                    radios.forEach(r => {
                                        r.onchange = (e) => updateState(e.target.value);
                                    });

                                    // Click on label wrapper triggers radio
                                    optAuto.onclick = (e) => {
                                        if (e.target.tagName !== 'INPUT') {
                                            radios[0].checked = true;
                                            updateState('auto');
                                        }
                                    };
                                    optCustom.onclick = (e) => {
                                        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
                                            radios[1].checked = true;
                                            updateState('custom');
                                        }
                                    };

                                    // Save
                                    document.getElementById('btn-save-edit').onclick = () => {
                                        const mode = document.querySelector('input[name="menu_mode"]:checked').value;
                                        if (mode === 'auto') {
                                            delete entry.customLabel; // CLEAR IT
                                        } else {
                                            entry.customLabel = customInput.value.trim() || entry.customLabel; // Keep old if empty? or allow empty?
                                            // If empty and custom selected, fallback to auto to prevent empty label
                                            if (!entry.customLabel) delete entry.customLabel;
                                        }
                                        this.renderListItems();
                                        wrapper.remove();
                                    };

                                    // Cancel
                                    document.getElementById('btn-cancel-edit').onclick = () => {
                                        wrapper.remove();
                                    };
                                };
                            }
                        }
                    }

                    // Delete button for Divider (and maybe marker)
                    if (entry.type !== 'ITEM' && !row.querySelector('.btn-delete-item')) {
                        // ...
                    }
                    // Add Delete button for Dividers Only for now?
                    if (entry.type === 'DIVIDER') {
                        const delBtn = row.querySelector('.btn-delete-item');
                        if (delBtn) {
                            delBtn.onclick = (e) => {
                                e.stopPropagation();
                                this.currentOrder.splice(index, 1);
                                this.renderListItems();
                            };
                        }
                    }

                    this.listEl.appendChild(row);
                } catch (innerE) {
                    console.error("Row render failed", innerE);
                }
            });

            this.initDragDrop();

        } catch (outerE) {
            this.listEl.innerHTML = `<div class="p-4 text-red-500">목록 렌더링 오류: ${outerE.message}</div>`;
        }
    }

    bindEvents() {
        this.btnAddDivider.onclick = () => {
            this.currentOrder.push({ type: 'DIVIDER' });
            this.renderListItems();
            // Scroll to bottom
            setTimeout(() => {
                this.listEl.scrollTop = this.listEl.scrollHeight;
            }, 100);
        };

        this.btnSave.onclick = async () => {
            try {
                this.btnSave.disabled = true;
                this.btnSave.textContent = "저장 중...";

                // 1. Prepare Data
                const cleanOrder = this.currentOrder.map(item => {
                    const cleanItem = { type: item.type };

                    if (item.type === 'MODAL') {
                        cleanItem.component = item.component;
                        cleanItem.docId = item.docId;
                        // MODALs always use customLabel as their primary label
                        if (item.customLabel) cleanItem.customLabel = item.customLabel;
                        if (item.id) cleanItem.id = item.id;
                        return cleanItem;
                    }

                    if (item.id) cleanItem.id = item.id;
                    if (item.customLabel && String(item.customLabel).trim().length > 0) {
                        cleanItem.customLabel = String(item.customLabel).trim();
                    }
                    return cleanItem;
                });

                console.group("[AdminMenuEditor] Save Process Initiated");
                console.log("1. Payload Prepared:", JSON.parse(JSON.stringify(cleanOrder))); // Deep copy for log

                // 2. Force Overwrite (merge: false) to ensure exact state
                await setDoc(this.navRef, {
                    sidebar_order: cleanOrder,
                    lastUpdated: new Date().toISOString(), // Metadata
                    updatedBy: 'admin'
                }, { merge: false });

                console.log("2. setDoc() Promise Resolved (Write Sent)");

                // 3. VERIFICATION READ (Immediate Check - Silent)
                const verifySnap = await getDoc(this.navRef);
                if (verifySnap.exists()) {
                    console.log("3. Verification Read SUCCESS:", verifySnap.data());
                } else {
                    console.error("3. Verification Read FAILED: Document not found after write!");
                    // Keep error alert if it actually fails
                    throw new Error("데이터를 보냈으나 DB에서 확인되지 않습니다. (Rules/Network 문제 가능성)");
                }
                console.groupEnd();

                console.log("[AdminMenuEditor] SAVE SUCCESS!");

                // 4. Success Message
                ModalManager.show(AlertModal, "저장 완료", "메뉴 텍스트와 순서가 서버에 저장되었습니다.\n(새로고침하여 100% 반영 여부를 확인하세요)");

            } catch (e) {
                console.error("[AdminMenuEditor] Save Failed", e);
                alert(`[CRITICAL ERROR] 저장 실패:\n${e.message}`);
                ModalManager.show(AlertModal, "저장 실패", e.message);
            } finally {
                this.btnSave.disabled = false;
                this.btnSave.textContent = "저장하기";
            }
        };
    }

    initDragDrop() {
        let draggedItem = null;
        const rows = this.listEl.querySelectorAll('[draggable="true"]');

        rows.forEach(row => {
            row.addEventListener('dragstart', (e) => {
                draggedItem = row;
                e.dataTransfer.effectAllowed = 'move';
                row.classList.add('opacity-50');
            });

            row.addEventListener('dragend', () => {
                draggedItem = null;
                rows.forEach(r => r.classList.remove('opacity-50', 'border-indigo-500', 'border-t-2', 'border-b-2'));
            });

            row.addEventListener('dragover', (e) => {
                e.preventDefault();
                return false;
            });

            row.addEventListener('dragenter', (e) => {
                e.preventDefault();
                // simple visual highlight
                row.classList.add('border-indigo-500');
            });

            row.addEventListener('dragleave', (e) => {
                row.classList.remove('border-indigo-500');
            });

            row.addEventListener('drop', (e) => {
                e.stopPropagation();
                if (draggedItem !== row) {
                    const fromIndex = parseInt(draggedItem.dataset.index);
                    const toIndex = parseInt(row.dataset.index);

                    // Move item in array
                    const item = this.currentOrder.splice(fromIndex, 1)[0];
                    this.currentOrder.splice(toIndex, 0, item);

                    this.renderListItems();
                }
                return false;
            });
        });
    }
}
